import { motion } from "framer-motion";
import {
  Heart,
  Cake,
  Users,
  Building2,
  Sparkles,
  Music,
  Camera,
  Disc3,
  ArrowRight,
} from "lucide-react";

const SERVICES = [
  {
    key: "mariage",
    title: "Mariage",
    desc:
      "Cérémonie, vin d'honneur, repas, ouverture de bal, soirée dansante : un accompagnement complet pour le plus beau jour.",
    icon: <Heart size={22} />,
    image:
      "https://customer-assets.emergentagent.com/job_adrien-sebert-dj/artifacts/ujbx7xzc_DSC04775.JPG",
    span: "lg:col-span-2 lg:row-span-2",
    featured: true,
  },
  {
    key: "anniversaire",
    title: "Anniversaire",
    desc:
      "30, 40, 50 ans, bar/bat-mitzvah… Une ambiance toutes générations, fluide et fédératrice.",
    icon: <Cake size={20} />,
    image:
      "https://customer-assets.emergentagent.com/job_adrien-sebert-dj/artifacts/kgg70tb4_IMG_8909.jpeg",
  },
  {
    key: "soiree-privee",
    title: "Soirée privée",
    desc:
      "Entre amis ou en famille : un set 100 % personnalisé, du début à la fin de la nuit.",
    icon: <Users size={20} />,
    image:
      "https://customer-assets.emergentagent.com/job_adrien-sebert-dj/artifacts/z7ohc5uw_IMG_8907.jpeg",
  },
  {
    key: "entreprise",
    title: "Soirée d'entreprise",
    desc:
      "Séminaires, gala, lancement produit : une prestation premium qui valorise votre image.",
    icon: <Building2 size={20} />,
    image:
      "https://static.prod-images.emergentagent.com/jobs/9056c5a8-6343-448e-8b30-18c6a8144e54/images/73ed1e77632da77ab8852f583988db66fa52bf1b6008756e2a2215215433b2bc.png",
  },
  {
    key: "evenement-public",
    title: "Événement public",
    desc:
      "Festivals, fêtes locales, ouvertures : sonorisation et animation à la hauteur de l'enjeu.",
    icon: <Sparkles size={20} />,
  },
  {
    key: "clubs-festivals",
    title: "Clubs & Festivals",
    desc:
      "DJ résident pour discothèques, festivals et soirées à thème. 20 ans en cabine — du club intimiste à la grande scène, tout est possible.",
    icon: <Disc3 size={20} />,
    image:
      "https://images.unsplash.com/photo-1571266028243-d220bc5e6b73?auto=format&fit=crop&w=1200&q=80",
  },
  {
    key: "sono-lumiere",
    title: "Sonorisation & éclairage",
    desc:
      "Matériel professionnel haut de gamme, dimensionné selon la salle et le nombre d'invités.",
    icon: <Music size={20} />,
    image:
      "https://customer-assets.emergentagent.com/job_adrien-sebert-dj/artifacts/ceq4aa8n_dsc08243_3_299052-176999564712674.jpeg",
  },
  {
    key: "options",
    title: "Options scéniques",
    desc:
      "Photobooth, fumée lourde au sol, étincelles scéniques, captation vidéo/photo souvenir.",
    icon: <Camera size={20} />,
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="eyebrow">Prestations</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Une offre <span className="gold-text-gradient">sur mesure</span> pour chaque événement.
            </h2>
            <p className="mt-4 text-zinc-400">
              Du mariage intimiste à la soirée d'entreprise haut de gamme, chaque
              prestation est pensée, préparée et personnalisée avec vous.
            </p>
          </div>
          <a
            href="#contact"
            data-testid="services-cta-quote"
            className="btn-outline-gold rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 self-start md:self-auto"
          >
            Demander un devis <ArrowRight size={15} />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:auto-rows-[230px]">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.55, delay: i * 0.05 }}
              data-testid={`service-${s.key}`}
              className={`relative overflow-hidden rounded-2xl group glass hover:border-[#D4AF37]/30 transition-all ${
                s.span || ""
              }`}
            >
              {s.image && (
                <>
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                      s.featured ? "opacity-70" : "opacity-25"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
                </>
              )}
              <div className="relative z-10 h-full p-6 md:p-7 flex flex-col">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#D4AF37]/15 text-[#D4AF37] mb-4">
                  {s.icon}
                </div>
                <h3 className={`font-semibold text-white tracking-tight ${s.featured ? "text-2xl md:text-3xl" : "text-lg"}`}>
                  {s.title}
                </h3>
                <p className={`mt-2 text-zinc-400 leading-relaxed ${s.featured ? "text-base max-w-md" : "text-sm"}`}>
                  {s.desc}
                </p>
                <a
                  href="#contact"
                  data-testid={`service-cta-${s.key}`}
                  className="mt-auto pt-5 inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#E9C766] transition-colors"
                >
                  En savoir plus <ArrowRight size={14} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
